import {getReviewConditionHistoryLocaleKey} from './reviews-condition-history-helper';
import {ReviewHistoryDataType} from './reviews-history';
import {
    CatalogHistoryChangeType,
    CatalogHistoryGroupType,
    HistoryConditionItemType,
    HistoryReplyItemType,
    ReviewHistoryFieldEnum,
    ReviewReplyHistoryActionEnum,
} from './reviews-history-helper-type';

function hasHistoryTextValue(value: unknown): boolean {
    if (typeof value !== 'string') {
        return false;
    }

    return value.trim().length > 0;
}

function isReplyHistoryField(field: string): boolean {
    return field.trim().toLowerCase() === ReviewHistoryFieldEnum.Reply;
}

function getReplyHistoryAction(oldValue: unknown, newValue: unknown): ReviewReplyHistoryActionEnum | null {
    const hasOld = hasHistoryTextValue(oldValue);
    const hasNew = hasHistoryTextValue(newValue);

    if (!hasOld && hasNew) {
        return ReviewReplyHistoryActionEnum.Created;
    }

    if (hasOld && hasNew) {
        return ReviewReplyHistoryActionEnum.Updated;
    }

    if (hasOld && !hasNew) {
        return ReviewReplyHistoryActionEnum.Deleted;
    }

    return null;
}

export function parseHistoryRatingValue(value: unknown): number | null {
    if (typeof value === 'number' && !Number.isNaN(value)) {
        return value;
    }

    if (typeof value === 'string') {
        const parsed = Number.parseFloat(value);

        return Number.isNaN(parsed) ? null : parsed;
    }

    return null;
}

export function formatHistoryRatingDisplay(rating: number): string {
    return rating.toFixed(1);
}

function isConditionHistoryField(field: string): boolean {
    return field === 'deleted' || field === 'moderated';
}

function getReviewHistoryLoggedAtSecondKey(loggedAt: string): string {
    const timestamp = Date.parse(loggedAt);

    if (Number.isNaN(timestamp)) {
        return loggedAt;
    }

    return new Date(Math.floor(timestamp / 1000) * 1000).toISOString();
}

function appendCatalogHistoryChange(
    groupsByDate: Map<string, CatalogHistoryGroupType>,
    item: ReviewHistoryDataType,
    normalizedField: ReviewHistoryFieldEnum.Rating | ReviewHistoryFieldEnum.Comment
): void {
    const loggedAtSecondKey = getReviewHistoryLoggedAtSecondKey(item.date);
    const existingGroup = groupsByDate.get(loggedAtSecondKey);
    const group: CatalogHistoryGroupType = existingGroup ?? {
        loggedAt: loggedAtSecondKey,
        source: 'history-group',
    };

    const change: CatalogHistoryChangeType = {
        newValue: item.newValue,
        oldValue: item.oldValue,
    };

    if (normalizedField === ReviewHistoryFieldEnum.Rating) {
        group.rating = change;
    } else {
        group.comment = change;
    }

    groupsByDate.set(loggedAtSecondKey, group);
}

export function splitAndGroupReviewHistory(items: Array<ReviewHistoryDataType>): {
    catalogEditGroups: Array<CatalogHistoryGroupType>;
    conditionItems: Array<HistoryConditionItemType>;
    replyItems: Array<HistoryReplyItemType>;
} {
    const conditionItems: Array<HistoryConditionItemType> = [];
    const replyItems: Array<HistoryReplyItemType> = [];
    const groupsByDate = new Map<string, CatalogHistoryGroupType>();

    items.forEach((item) => {
        if (isConditionHistoryField(item.field)) {
            const conditionLocaleKey = getReviewConditionHistoryLocaleKey(item.field, item.oldValue, item.newValue);

            if (conditionLocaleKey) {
                conditionItems.push({
                    ...item,
                    loggedAt: getReviewHistoryLoggedAtSecondKey(item.date),
                    source: 'history-condition',
                });
            }

            return;
        }

        if (isReplyHistoryField(item.field)) {
            const replyAction = getReplyHistoryAction(item.oldValue, item.newValue);

            if (replyAction) {
                replyItems.push({
                    action: replyAction,
                    loggedAt: getReviewHistoryLoggedAtSecondKey(item.date),
                    newValue: item.newValue,
                    oldValue: item.oldValue,
                    source: 'history-reply',
                });
            }

            return;
        }

        const normalizedField = item.field.trim().toLowerCase();

        if (normalizedField === ReviewHistoryFieldEnum.Rating) {
            appendCatalogHistoryChange(groupsByDate, item, ReviewHistoryFieldEnum.Rating);
        } else if (normalizedField === ReviewHistoryFieldEnum.Comment) {
            appendCatalogHistoryChange(groupsByDate, item, ReviewHistoryFieldEnum.Comment);
        }
    });

    const catalogEditGroups = [...groupsByDate.values()].filter((group) => group.rating || group.comment);

    return {catalogEditGroups, conditionItems, replyItems};
}
